<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import PortalThreadAvatar from './PortalThreadAvatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversation: { type: Object, required: true },
});

const store = useStore();
const expandedEmail = ref('');
const savingEmail = ref('');

const normalizeEmail = value => String(value || '').trim().toLowerCase();

const people = computed(() => {
  const attributes = props.conversation.additional_attributes || {};
  const custom = props.conversation.custom_attributes || {};
  const raw =
    custom.tnm_portal_recipient_roster ||
    attributes.tnm_portal_participants ||
    [];
  const seen = new Set();
  return (Array.isArray(raw) ? raw : [])
    .map(person => ({
      email: normalizeEmail(person?.email),
      name: String(person?.name || person?.email || '').trim(),
    }))
    .filter(
      person => person.email && !seen.has(person.email) && !!seen.add(person.email)
    );
});

const recipientEmails = computed(() => {
  const configured =
    props.conversation.custom_attributes?.tnm_portal_recipient_emails;
  const allowed = new Set(people.value.map(person => person.email));
  const emails = Array.isArray(configured)
    ? configured.map(normalizeEmail).filter(email => allowed.has(email))
    : [];
  return emails.length
    ? new Set(emails)
    : new Set(people.value.map(person => person.email));
});

const activePeople = computed(() =>
  people.value.filter(person => recipientEmails.value.has(person.email))
);
const availablePeople = computed(() =>
  people.value.filter(person => !recipientEmails.value.has(person.email))
);

const updateRecipients = async emails => {
  savingEmail.value = 'saving';
  const customAttributes = {
    ...(props.conversation.custom_attributes || {}),
    tnm_portal_recipient_emails: emails,
  };
  try {
    await store.dispatch('updateCustomAttributes', {
      conversationId: props.conversation.id,
      customAttributes,
    });
    useAlert('Thread people updated');
  } catch (error) {
    useAlert('Could not update the thread people. Please try again.');
  } finally {
    savingEmail.value = '';
  }
};

const removePerson = email =>
  updateRecipients([...recipientEmails.value].filter(value => value !== email));
const addPerson = email => updateRecipients([...recipientEmails.value, email]);
const toggleDetails = email => {
  expandedEmail.value = expandedEmail.value === email ? '' : email;
};
</script>

<template>
  <section
    class="mx-3 mb-4 overflow-hidden border rounded-xl border-n-weak bg-n-solid-1"
  >
    <div
      class="flex items-center justify-between px-3 py-3 border-b border-n-weak"
    >
      <div class="flex items-center gap-2 min-w-0">
        <span class="i-ph-users-bold text-n-slate-11 size-4" />
        <div class="min-w-0">
          <h2 class="m-0 text-sm font-medium text-n-slate-12">
            People on this thread
          </h2>
          <p class="m-0 text-xs text-n-slate-11">
            {{ activePeople.length }} selected for public portal updates
          </p>
        </div>
      </div>
      <div class="flex -space-x-2 rtl:space-x-reverse">
        <PortalThreadAvatar
          v-for="person in activePeople.slice(0, 4)"
          :key="person.email"
          :person="person"
          :size="24"
        />
      </div>
    </div>

    <div class="p-2">
      <article
        v-for="person in activePeople"
        :key="person.email"
        class="rounded-lg hover:bg-n-alpha-1"
      >
        <div class="flex items-center gap-2 px-2 py-2">
          <button
            type="button"
            class="flex items-center flex-1 min-w-0 gap-2 text-left"
            @click="toggleDetails(person.email)"
          >
            <PortalThreadAvatar :person="person" :size="28" />
            <span class="min-w-0">
              <span class="block text-sm font-medium truncate text-n-slate-12">
                {{ person.name || person.email }}
              </span>
              <span class="block text-xs truncate text-n-slate-11">
                {{ person.email }}
              </span>
            </span>
          </button>
          <Button
            v-tooltip.top="'Remove from public updates'"
            ghost slate xs
            icon="i-lucide-x"
            :disabled="savingEmail === 'saving'"
            @click="removePerson(person.email)"
          />
        </div>
        <div
          v-if="expandedEmail === person.email"
          class="px-3 pb-3 ml-8 text-xs text-n-slate-11"
        >
          This person has portal access on the linked record and will receive
          future public updates from this thread. Internal notes are never sent.
        </div>
      </article>

      <div
        v-if="availablePeople.length"
        class="pt-2 mt-2 border-t border-n-weak"
      >
        <p class="px-2 mb-1 text-xs font-medium text-n-slate-11">
          Add a linked portal person
        </p>
        <div
          v-for="person in availablePeople"
          :key="person.email"
          class="flex items-center gap-2 px-2 py-1.5"
        >
          <PortalThreadAvatar :person="person" :size="24" />
          <span class="flex-1 min-w-0 text-sm truncate text-n-slate-12">
            {{ person.name || person.email }}
          </span>
          <Button
            label="Add"
            ghost teal xs
            :disabled="savingEmail === 'saving'"
            @click="addPerson(person.email)"
          />
        </div>
      </div>
    </div>
  </section>
</template>
